import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useRequest } from 'alova/client'
import { Popover, Spin, message } from 'antd'
import AIcon from '@/components/A/AIcon/AIcon'
import styles from './StatisticCompareInfoPage.module.less'
import { GENDER_MAPS, INDUSTRY_CATEGORY_MAPS, TARGET_MARKTS_MAPS } from '@/i18nEnumConsts'
import { commonUtils } from '@/helpers/commonUtils'
import useProductType from '@/helpers/commonHooks/useProductType'
import { getProductMode, isPortraitProductCategory } from '@/helpers/productCategory'
import { getIndustryDisplayName } from '@/helpers/industry'
import { getProductDashboardLink } from '@/router/pageConst'
import { api } from '@/api'
import { compare } from '@/api/compare/compare'

type MetricTabKey = 'dimension' | 'moment' | 'attitude'

type MetricRow = {
  key: string
  labelKey: string
  labelI18nKey?: string
  defaultLabel: string
  metricField: keyof compare.CompareProjectMetrics
}

type MetricSortDirection = 'asc' | 'desc' | 'origin'

type MetricSortState = {
  key: string
  direction: MetricSortDirection
}

const TABBED_METRIC_ROWS: Record<MetricTabKey, MetricRow[]> = {
  dimension: [
    { key: 'attention', labelKey: 'attention', defaultLabel: '注意力', metricField: 'attentionForce' },
    { key: 'infectivity', labelKey: 'infectivity', defaultLabel: '感染力', metricField: 'emotionalForce' },
    { key: 'delivery', labelKey: 'delivery', defaultLabel: '传达力', metricField: 'messageForce' },
    { key: 'brand', labelKey: 'brand', defaultLabel: '品牌力', metricField: 'brandForce' },
    { key: 'persuasion', labelKey: 'persuasion', defaultLabel: '说服力', metricField: 'persuasiveForce' },
  ],
  moment: [
    { key: 'gazeRecognition', labelKey: 'gazeRecognition', defaultLabel: '注视识别', metricField: 'emrScore' },
    { key: 'emotionRecognition', labelKey: 'emotionRecognition', defaultLabel: '情绪识别', metricField: 'emotionScore' },
    { key: 'engagementRecognition', labelKey: 'engagementRecognition', defaultLabel: '投入识别', metricField: 'cognitiveScore' },
    { key: 'goldenSeconds', labelKey: 'goldenSeconds', defaultLabel: '黄金3秒', metricField: 'firstThreeScore' },
    { key: 'messageMoment', labelKey: 'messageMoment', defaultLabel: '信息时刻', metricField: 'keyInformationScore' },
    { key: 'brandMoment', labelKey: 'brandMoment', defaultLabel: '品牌时刻', metricField: 'brandConnectionScore' },
    { key: 'productMoment', labelKey: 'productMoment', defaultLabel: '兴趣时刻', metricField: 'interestingEnagementScore' },
    { key: 'endingSeconds', labelKey: 'endingSeconds', defaultLabel: '结尾3秒', metricField: 'lastThreeScore' },
  ],
  attitude: [
    { key: 'likeability', labelKey: 'likeability', defaultLabel: '喜爱度', metricField: 'liking' },
    { key: 'newInformation', labelKey: 'newInformation', defaultLabel: '新信息', metricField: 'newInformation' },
    { key: 'messageRelevance', labelKey: 'messageRelevance', defaultLabel: '相关度', metricField: 'messageRelevance' },
    { key: 'comprehension', labelKey: 'comprehension', defaultLabel: '理解度', metricField: 'comprehension' },
    { key: 'credibility', labelKey: 'credibility', defaultLabel: '可信度', metricField: 'credibility' },
    { key: 'brandLinkage', labelKey: 'brandLinkage', defaultLabel: '契合度', metricField: 'brandLinkage' },
    { key: 'uniqueness', labelKey: 'uniqueness', defaultLabel: '独特性', metricField: 'uniqueness' },
    { key: 'learnMoreIntent', labelKey: 'learnMoreIntent', defaultLabel: '了解意愿', metricField: 'willingnessToLearnMore' },
    { key: 'purchaseIntent', labelKey: 'purchaseIntent', defaultLabel: '购买意愿', metricField: 'purchaseIntention' },
    { key: 'recommendIntent', labelKey: 'recommendIntent', defaultLabel: '推荐意愿', metricField: 'shareability' },
    { key: 'brandLikeabilityLift', labelKey: 'brandLikeabilityLift', defaultLabel: '品牌好感提升', metricField: 'brandLikeabilityLift' },
    { key: 'purchaseIntentionLift', labelKey: 'purchaseIntentionLift', defaultLabel: '购买意愿提升', metricField: 'purchaseIntentionLift' },
  ],
}

const CREATIVE_DIAGNOSIS_METRIC_ROWS: MetricRow[] = [
  {
    key: 'attentionIndex',
    labelKey: 'attentionIndex',
    labelI18nKey: 'dashboard.projectDetailPage.subAnalysisDescription.attentionIndex',
    defaultLabel: '注意指数',
    metricField: 'emrScore',
  },
  {
    key: 'emotionIndex',
    labelKey: 'emotionIndex',
    labelI18nKey: 'dashboard.projectDetailPage.subAnalysisDescription.emotionIndex',
    defaultLabel: '情绪指数',
    metricField: 'emotionScore',
  },
  {
    key: 'cognitionIndex',
    labelKey: 'cognitionIndex',
    labelI18nKey: 'dashboard.projectDetailPage.subAnalysisDescription.cognitionIndex',
    defaultLabel: '认知指数',
    metricField: 'cognitiveScore',
  },
  {
    key: 'goldenSeconds',
    labelKey: 'goldenSeconds',
    labelI18nKey: 'dashboard.projectDetailPage.subAnalysisDescription.theGolden3Seconds',
    defaultLabel: '黄金3秒',
    metricField: 'firstThreeScore',
  },
  {
    key: 'messageMoment',
    labelKey: 'messageMoment',
    labelI18nKey: 'dashboard.projectDetailPage.subAnalysisDescription.informationTransmission',
    defaultLabel: '信息时刻',
    metricField: 'keyInformationScore',
  },
  {
    key: 'brandMoment',
    labelKey: 'brandMoment',
    labelI18nKey: 'dashboard.projectDetailPage.subAnalysisDescription.brandRelevance',
    defaultLabel: '品牌时刻',
    metricField: 'brandConnectionScore',
  },
  {
    key: 'productMoment',
    labelKey: 'productMoment',
    labelI18nKey: 'dashboard.projectDetailPage.subAnalysisDescription.interestStimulation',
    defaultLabel: '兴趣时刻',
    metricField: 'interestingEnagementScore',
  },
  {
    key: 'endingSeconds',
    labelKey: 'endingSeconds',
    labelI18nKey: 'dashboard.projectDetailPage.subAnalysisDescription.theEnding3Seconds',
    defaultLabel: '结尾3秒',
    metricField: 'lastThreeScore',
  },
]

const METRIC_FIELD_MAP: Record<string, keyof compare.CompareProjectMetrics> = {
  attention: 'attentionForce',
  infectivity: 'emotionalForce',
  delivery: 'messageForce',
  brand: 'brandForce',
  persuasion: 'persuasiveForce',
  gazeRecognition: 'emrScore',
  emotionRecognition: 'emotionScore',
  engagementRecognition: 'cognitiveScore',
  goldenSeconds: 'firstThreeScore',
  brandMoment: 'brandConnectionScore',
  productMoment: 'interestingEnagementScore',
  messageMoment: 'keyInformationScore',
  endingSeconds: 'lastThreeScore',
  likeability: 'liking',
  newInformation: 'newInformation',
  messageRelevance: 'messageRelevance',
  comprehension: 'comprehension',
  credibility: 'credibility',
  brandLinkage: 'brandLinkage',
  uniqueness: 'uniqueness',
  learnMoreIntent: 'willingnessToLearnMore',
  purchaseIntent: 'purchaseIntention',
  recommendIntent: 'shareability',
  brandLikeabilityLift: 'brandLikeabilityLift',
  purchaseIntentionLift: 'purchaseIntentionLift',
  attentionIndex: 'emrScore',
  emotionIndex: 'emotionScore',
  cognitionIndex: 'cognitiveScore',
}

const HORIZONTAL_SCROLL_THUMB_INSET = 4
const EMPTY_COMPARE_PROJECTS: compare.CompareProject[] = []

function StatisticCompareInfoPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { productType } = useProductType()
  const reportId = Number(searchParams.get('reportId') || 0)
  const productMode = getProductMode(productType)
  const showMetricTabs = productMode === 'quick' || productMode === 'survey'
  const [activeTab, setActiveTab] = useState<MetricTabKey>('dimension')
  const [projectSortState, setProjectSortState] = useState<MetricSortState>({ key: '', direction: 'origin' })
  const lastReportRequestKeyRef = useRef('')
  const {
    data,
    loading,
    send: sendReportDetail,
    update: updateReportDetail,
  } = useRequest(api.compare.compareReportDetail, {
    immediate: false,
    initialData: { data: undefined },
  })
  const { send: confirmDeletedProjects, loading: confirmingDeletedProjects } = useRequest(
    api.compare.compareReportConfirmDeletedProjects,
    { immediate: false }
  )
  const report = data?.data
  const projects = report?.projects || EMPTY_COMPARE_PROJECTS
  const deletedProjects = report?.deletedProjects || []
  const reportName = report?.reportName || t('dashboard.dataComparisonInfoPage.defaultReportName', {
    defaultValue: '对比报告名称',
  })
  const deletedProjectListSeparator = t('dashboard.dataComparisonInfoPage.listSeparator', {
    defaultValue: '、',
  })
  const normScore = getMetricNorm(report?.metricNorms, 'overallScore')

  const displayProjects = useMemo(() => projects.slice(0, 10), [projects])
  const isMixedCreativeComparison = hasMixedPortraitCreativeProjects(productType, displayProjects)
  const metricRows = showMetricTabs
    ? TABBED_METRIC_ROWS[activeTab]
    : CREATIVE_DIAGNOSIS_METRIC_ROWS
  const visibleMetricRows = useMemo(
    () => metricRows.filter((row) => hasVisibleMetricScore(displayProjects, row.key)),
    [displayProjects, metricRows]
  )
  const sortedProjects = useSortedProjects(displayProjects, projectSortState)

  useEffect(() => {
    if (!reportId) {
      message.warning(t('dashboard.dataComparisonInfoPage.reportNotFound', { defaultValue: '报告不存在' }))
      navigate(getProductDashboardLink(productType, 'statisticCompare'))
      return
    }
    const requestKey = `${productType}:${reportId}`
    if (lastReportRequestKeyRef.current === requestKey) {
      return
    }
    lastReportRequestKeyRef.current = requestKey
    sendReportDetail(reportId)
  }, [reportId, sendReportDetail, navigate, productType, t])

  useEffect(() => {
    setProjectSortState((current) => (
      current.key === '' && current.direction === 'origin'
        ? current
        : { key: '', direction: 'origin' }
    ))
  }, [displayProjects])

  useEffect(() => {
    setProjectSortState((current) => {
      if (current.direction === 'origin' || current.key === 'score') {
        return current
      }
      return visibleMetricRows.some((row) => row.key === current.key)
        ? current
        : { key: '', direction: 'origin' }
    })
  }, [visibleMetricRows])

  const goBackToList = () => {
    navigate(getProductDashboardLink(productType, 'statisticCompare'))
  }

  const goEdit = () => {
    const params = new URLSearchParams({
      reportId: `${reportId}`,
      from: 'info',
    })
    navigate(`${getProductDashboardLink(productType, 'statisticCompareStart')}?${params.toString()}`)
  }

  const goProjectDetail = (projectId: number) => {
    navigate(getProductDashboardLink(productType, 'projectDetail'), {
      state: {
        id: projectId,
        returnTo: `${location.pathname}${location.search}`,
      },
    })
  }

  const updateProjectSort = (key: string) => {
    setProjectSortState((current) => {
      if (current.key !== key) {
        return { key, direction: 'asc' }
      }
      if (current.direction === 'asc') {
        return { key, direction: 'desc' }
      }
      return { key: '', direction: 'origin' }
    })
  }

  const handleConfirmDeletedProjects = () => {
    confirmDeletedProjects(reportId).then((result) => {
      updateReportDetail({ data: result })
    })
  }

  return (
    <div className={styles.statisticCompareInfoPage} data-testid='statistic-compare-info-page'>
      <button
        type='button'
        className={styles.backButton}
        data-testid='statistic-compare-info-back-btn'
        onClick={goBackToList}
      >
        <span className={styles.backIconBox}>
          <AIcon type='icon-line-global_left' width={14} height={14} />
        </span>
        <span>{t('dashboard.dataComparisonInfoPage.back', { defaultValue: '返回' })}</span>
      </button>

      <div className={styles.contentPanel}>
        {loading && !report ? (
          <div className={styles.loadingBox}><Spin /></div>
        ) : null}
        {report ? (
          <>
            <section className={styles.reportHeader}>
              <SectionTitle>{reportName}</SectionTitle>
              <button
                type='button'
                className={styles.modifyButton}
                data-testid='statistic-compare-info-modify-btn'
                onClick={goEdit}
              >
                <AIcon type='icon-line-global_add1' width={16} height={16} />
                <span>{t('dashboard.dataComparisonInfoPage.modify', { defaultValue: '修改' })}</span>
              </button>
            </section>

            {deletedProjects.length > 0 ? (
              <div className={styles.deletedProjectTip}>
                <span>
                  {t('dashboard.dataComparisonInfoPage.deletedProjectTip', {
                    names: deletedProjects.map((project) => project.projectName).join(deletedProjectListSeparator),
                    defaultValue: '以下项目：{{names}} 已被删除，确认后将更新对比报告并移除已删除项目',
                  })}
                </span>
                <button
                  type='button'
                  onClick={handleConfirmDeletedProjects}
                  disabled={confirmingDeletedProjects}
                >
                  {t('dashboard.dataComparisonInfoPage.confirmDeletedProjects', { defaultValue: '确认' })}
                </button>
              </div>
            ) : null}

            <ComparisonTable
              projects={sortedProjects}
              normScore={normScore}
              isMixedCreativeComparison={isMixedCreativeComparison}
              i18nLanguage={i18n.language}
              sortState={projectSortState}
              onSortAdeffScore={() => updateProjectSort('score')}
              onProjectDetail={goProjectDetail}
            />

            <section className={styles.metricSection}>
              <SectionTitle>
                {t('dashboard.dataComparisonInfoPage.metricScore', { defaultValue: '指标得分' })}
              </SectionTitle>
              {showMetricTabs ? (
                <div className={styles.metricTabs} data-testid='statistic-compare-info-metric-tabs'>
                  {([
                    { key: 'dimension', label: t('dashboard.dataComparisonInfoPage.tabDimension', { defaultValue: '五大维度' }) },
                    { key: 'moment', label: t('dashboard.dataComparisonInfoPage.tabMoment', { defaultValue: '关键时刻' }) },
                    { key: 'attitude', label: t('dashboard.dataComparisonInfoPage.tabAttitude', { defaultValue: '消费者态度' }) },
                  ] as const).map((tab) => (
                    <button
                      key={tab.key}
                      type='button'
                      className={styles.metricTab}
                      data-active={activeTab === tab.key}
                      onClick={() => setActiveTab(tab.key)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              ) : null}
              <MetricTable
                projects={sortedProjects}
                rows={visibleMetricRows}
                metricNorms={report.metricNorms}
                isMixedCreativeComparison={isMixedCreativeComparison}
                sortState={projectSortState}
                onSortMetric={(row) => updateProjectSort(row.key)}
                onProjectDetail={goProjectDetail}
              />
            </section>
          </>
        ) : null}
      </div>
    </div>
  )
}

function SectionTitle(props: { children: React.ReactNode }) {
  return <h2 className={styles.sectionTitle}>{props.children}</h2>
}

function ComparisonTable(props: {
  projects: compare.CompareProject[]
  normScore: number | string
  isMixedCreativeComparison: boolean
  i18nLanguage: string
  sortState: MetricSortState
  onSortAdeffScore: () => void
  onProjectDetail: (projectId: number) => void
}) {
  const { t } = useTranslation()
  const {
    projects,
    normScore,
    isMixedCreativeComparison,
    i18nLanguage,
    sortState,
    onSortAdeffScore,
    onProjectDetail,
  } = props
  const gridStyle = useGridStyle(projects.length)
  const { scrollRef, isScrollable, scrollState, setScrollLeft } = useHorizontalScrollState([projects.length])

  const lng = i18nLanguage as keyof typeof INDUSTRY_CATEGORY_MAPS
  const industryMap = INDUSTRY_CATEGORY_MAPS[lng] as Record<string, string>
  const genderMap = GENDER_MAPS[lng] as Record<string, string>
  const targetMarketMap = TARGET_MARKTS_MAPS[lng] as Record<string, string>

  const infoRows = [
    {
      key: 'score',
      label: t('dashboard.dataComparisonInfoPage.adeffScore', { defaultValue: 'Adeff 得分' }),
      norm: normScore,
      render: (project: compare.CompareProject) => getProjectTotalScore(project),
      emphasis: true,
    },
    {
      key: 'brandName',
      label: t('dashboard.dataComparisonInfoPage.brandName', { defaultValue: '品牌名称' }),
      render: (project: compare.CompareProject) => project.brandName || '-',
    },
    {
      key: 'projectName',
      label: t('dashboard.dataComparisonInfoPage.projectName', { defaultValue: '项目名称' }),
      render: (project: compare.CompareProject) => project.projectName || '-',
    },
    {
      key: 'industry',
      label: t('dashboard.dataComparisonInfoPage.industry', { defaultValue: '行业' }),
      render: (project: compare.CompareProject) => (
        project.brandIndustry ? getIndustryDisplayName(project.brandIndustry, industryMap) : '-'
      ),
    },
    {
      key: 'targetMarket',
      label: t('dashboard.dataComparisonInfoPage.targetMarket', { defaultValue: '目标市场' }),
      render: (project: compare.CompareProject) => (
        targetMarketMap[project.adTargetMarket] || project.adTargetMarket || '-'
      ),
    },
    {
      key: 'gender',
      label: t('dashboard.dataComparisonInfoPage.gender', { defaultValue: 'TA性别' }),
      render: (project: compare.CompareProject) => (
        genderMap[project.adCustomerGender] || project.adCustomerGender || '-'
      ),
    },
    {
      key: 'age',
      label: t('dashboard.dataComparisonInfoPage.age', { defaultValue: 'TA年龄' }),
      render: (project: compare.CompareProject) => (
        project.adCustomerAgeStart || project.adCustomerAgeEnd
          ? t('dashboard.projectDetailPage.infoDrawer.ageRange', {
            start: project.adCustomerAgeStart,
            end: project.adCustomerAgeEnd,
          })
          : '-'
      ),
    },
  ]
  const totalScores = projects.map(getProjectTotalScore)
  const maxTotalScore = Math.max(
    ...totalScores.filter((score): score is number => typeof score === 'number'),
    0
  )

  return (
    <>
    <div
      ref={scrollRef}
      className={styles.tableOuter}
      data-scrollable={isScrollable}
    >
      <div className={styles.compareTable}>
        <div className={classNames(styles.tableRow, styles.projectHeadRow)} style={gridStyle}>
          <div className={classNames(styles.tableCell, styles.labelCell)}>
            {t('dashboard.dataComparisonInfoPage.adProject', { defaultValue: '广告项目' })}
          </div>
          {projects.map((project) => (
            <ProjectColumnHeader
              key={project.id}
              project={project}
              onProjectDetail={onProjectDetail}
            />
          ))}
        </div>

        {infoRows.map((row) => (
          <div key={row.key} className={styles.tableRow} style={gridStyle}>
            <div className={classNames(styles.tableCell, styles.labelCell)}>
              {row.key === 'score' ? (
                <button
                  type='button'
                  className={styles.metricSortButton}
                  onClick={onSortAdeffScore}
                >
                  <span>{row.label}</span>
                  <span
                    className={styles.sortIcon}
                    data-direction={sortState.key === row.key ? sortState.direction : 'origin'}
                  >
                    <span className={styles.sortAscIcon} />
                    <span className={styles.sortDescIcon} />
                  </span>
                </button>
              ) : (
                <span>{row.label}</span>
              )}
              {'norm' in row ? (
                <span className={styles.normText}>
                  ({t('dashboard.dataComparisonInfoPage.normLabel', { defaultValue: '基准值' })}{' '}
                  <BenchmarkValue
                    value={isMixedCreativeComparison ? '-' : formatNormValue(row.norm)}
                    showTip={isMixedCreativeComparison}
                  />)
                </span>
              ) : null}
            </div>
            {projects.map((project, index) => {
              const value = row.render(project)
              const score = totalScores[index]
              const isBestAdeffScore = row.emphasis
                && typeof score === 'number'
                && score === maxTotalScore
              const trend = row.emphasis && !isBestAdeffScore && typeof score === 'number'
                ? getScoreTrend(score, isMixedCreativeComparison ? '-' : normScore)
                : undefined

              return (
                <div
                  key={`${row.key}-${project.id}`}
                  className={classNames(styles.tableCell, styles.valueCell)}
                >
                  {isBestAdeffScore ? (
                    <span className={styles.bestAdeffScoreContent}>
                      <img
                        className={styles.bestAdeffScoreBg}
                        src={commonUtils.getImagePath('projectDetailPage/score-pass-bg.png')}
                        alt=''
                      />
                      <span>{value}</span>
                    </span>
                  ) : value}
                  {trend ? (
                    <AIcon
                      className={classNames(styles.trendIcon, {
                        [styles.up]: trend === 'up',
                        [styles.down]: trend === 'down',
                      })}
                      type='icon-line-global_arrow'
                      width={14}
                      height={14}
                    />
                  ) : null}
                </div>
              )
            })}
          </div>
        ))}

        {projects.length === 0 ? (
          <div className={styles.emptyHint}>
            {t('dashboard.dataComparisonInfoPage.emptyProject', {
              defaultValue: '请选择项目后查看对比信息',
            })}
          </div>
        ) : null}
      </div>
    </div>
      <HorizontalScrollBar
        isScrollable={isScrollable}
        scrollState={scrollState}
        onScrollLeftChange={setScrollLeft}
      />
    </>
  )
}

function MetricTable(props: {
  projects: compare.CompareProject[]
  rows: MetricRow[]
  metricNorms?: compare.CompareReportDetail['metricNorms']
  isMixedCreativeComparison: boolean
  sortState: MetricSortState
  onSortMetric: (row: MetricRow) => void
  onProjectDetail: (projectId: number) => void
}) {
  const { t } = useTranslation()
  const {
    projects,
    rows,
    metricNorms,
    isMixedCreativeComparison,
    sortState,
    onSortMetric,
    onProjectDetail,
  } = props
  const gridStyle = useGridStyle(projects.length)
  const { scrollRef, isScrollable, scrollState, setScrollLeft } = useHorizontalScrollState([projects.length, rows.length])

  return (
    <>
    <div
      ref={scrollRef}
      className={styles.tableOuter}
      data-scrollable={isScrollable}
    >
      <div className={styles.compareTable}>
        <div className={classNames(styles.tableRow, styles.metricHeadRow)} style={gridStyle}>
          <div className={classNames(styles.tableCell, styles.labelCell)}>
            {t('dashboard.dataComparisonInfoPage.adProject', { defaultValue: '广告项目' })}
          </div>
          {projects.map((project) => (
            <ProjectColumnHeader
              key={project.id}
              project={project}
              compact
              onProjectDetail={onProjectDetail}
            />
          ))}
        </div>

        {rows.map((row) => {
          const scores = projects.map((project) => getMetricScore(project, row.key))
          const norm = getMetricNorm(metricNorms, row.metricField)
          const maxScore = Math.max(
            ...scores.filter((score): score is number => typeof score === 'number'),
            0
          )
          return (
            <div key={row.key} className={styles.tableRow} style={gridStyle}>
              <div className={classNames(styles.tableCell, styles.labelCell)}>
                <button
                  type='button'
                  className={styles.metricSortButton}
                  onClick={() => onSortMetric(row)}
                >
                  <span>
                    {t(row.labelI18nKey || `dashboard.dataComparisonInfoPage.metricLabels.${row.labelKey}`, {
                      defaultValue: row.defaultLabel,
                    })}
                  </span>
                  <span
                    className={styles.sortIcon}
                    data-direction={sortState.key === row.key ? sortState.direction : 'origin'}
                  >
                    <span className={styles.sortAscIcon} />
                    <span className={styles.sortDescIcon} />
                  </span>
                </button>
                <span className={styles.normText}>
                  ({t('dashboard.dataComparisonInfoPage.normLabel', { defaultValue: '基准值' })}{' '}
                  <BenchmarkValue
                    value={isMixedCreativeComparison ? '-' : formatNormValue(norm)}
                    showTip={isMixedCreativeComparison}
                  />)
                </span>
              </div>
              {projects.map((project, projectIndex) => {
                const score = scores[projectIndex]
                const isBestScore = typeof score === 'number' && maxScore > 0 && score === maxScore
                const trend = typeof score === 'number' && !isBestScore
                  ? getScoreTrend(score, isMixedCreativeComparison ? '-' : norm)
                  : undefined
                return (
                  <div
                    key={`${row.key}-${project.id}`}
                    className={classNames(styles.tableCell, styles.valueCell)}
                  >
                    {isBestScore ? (
                      <span className={styles.bestAdeffScoreContent}>
                        <img
                          className={styles.bestAdeffScoreBg}
                          src={commonUtils.getImagePath('projectDetailPage/score-pass-bg.png')}
                          alt=''
                        />
                        <span>{score}</span>
                      </span>
                    ) : score}
                    {trend ? (
                      <AIcon
                        className={classNames(styles.trendIcon, {
                          [styles.up]: trend === 'up',
                          [styles.down]: trend === 'down',
                        })}
                        type='icon-line-global_arrow'
                        width={14}
                        height={14}
                      />
                    ) : null}
                  </div>
                )
              })}
            </div>
          )
        })}

        {projects.length === 0 ? (
          <div className={styles.emptyHint}>
            {t('dashboard.dataComparisonInfoPage.emptyProject', {
              defaultValue: '请选择项目后查看对比信息',
            })}
          </div>
        ) : null}
      </div>
    </div>
      <HorizontalScrollBar
        isScrollable={isScrollable}
        scrollState={scrollState}
        onScrollLeftChange={setScrollLeft}
      />
    </>
  )
}

function BenchmarkValue(props: {
  value: number | string
  showTip: boolean
}) {
  const { t } = useTranslation()
  return (
    <>
      <b>{props.value}</b>
      {props.showTip ? (
        <Popover
          placement='rightTop'
          destroyTooltipOnHide
          overlayClassName={styles.normTipPopover}
          content={(
            <div className={styles.normTipPopoverContent}>
              {t('dashboard.dataComparisonInfoPage.mixedCreativeBenchmarkTip', {
                defaultValue: '当同时对比常规创意和开屏创意时，无基准值',
              })}
            </div>
          )}
        >
          <span className={styles.normTipIcon}>
            <AIcon width={18} type='icon-line-global_question' />
          </span>
        </Popover>
      ) : null}
    </>
  )
}

function ProjectColumnHeader(props: {
  project: compare.CompareProject
  compact?: boolean
  onProjectDetail: (projectId: number) => void
}) {
  const { t } = useTranslation()
  const { project, compact, onProjectDetail } = props
  return (
    <div className={classNames(styles.tableCell, styles.projectColumnHeader, {
      [styles.compact]: compact,
    })}
    >
      <p className={styles.projectColumnTitle}>{project.adName || project.projectName || '-'}</p>
      <CoverImage src={project.coverPath || commonUtils.getImagePath('common/common-empty.png')} />
      <button
        type='button'
        className={styles.detailLink}
        onClick={() => onProjectDetail(project.id)}
      >
        {t('dashboard.dataComparisonInfoPage.viewProjectDetail', { defaultValue: '查看项目详情' })}
      </button>
    </div>
  )
}

function CoverImage(props: { src: string }) {
  const fallbackSrc = commonUtils.getImagePath('common/common-empty.png')
  const src = props.src || fallbackSrc
  return (
    <img
      className={styles.coverImage}
      src={src}
      alt=''
      loading='eager'
      decoding='async'
      onError={(event) => {
        const img = event.currentTarget
        if (img.src !== fallbackSrc) {
          img.src = fallbackSrc
        }
      }}
    />
  )
}

function useGridStyle(projectCount: number) {
  return useMemo<React.CSSProperties>(() => ({
    gridTemplateColumns: `176px repeat(${Math.max(projectCount, 1)}, 176px)`,
  }), [projectCount])
}

function useSortedProjects(
  projects: compare.CompareProject[],
  sortState: MetricSortState
) {
  return useMemo(() => {
    if (sortState.direction === 'origin' || !sortState.key) {
      return projects
    }
    const sortDirection = sortState.direction
    return projects
      .map((project, index) => ({ project, index }))
      .sort((a, b) => {
        const scoreComparison = compareSortableScores(
          getProjectSortScore(a.project, sortState.key),
          getProjectSortScore(b.project, sortState.key),
          sortDirection
        )
        if (scoreComparison === 0) {
          return a.index - b.index
        }
        return scoreComparison
      })
      .map((item) => item.project)
  }, [projects, sortState])
}

function useHorizontalScrollState(deps: React.DependencyList) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [isScrollable, setIsScrollable] = useState(false)
  const [scrollState, setScrollState] = useState({
    clientWidth: 0,
    scrollLeft: 0,
    scrollWidth: 0,
  })

  const updateScrollState = useCallback(() => {
    const element = scrollRef.current
    if (!element) return
    setIsScrollable(element.scrollWidth > element.clientWidth + 1)
    setScrollState({
      clientWidth: element.clientWidth,
      scrollLeft: element.scrollLeft,
      scrollWidth: element.scrollWidth,
    })
  }, [])

  const setScrollLeft = useCallback((scrollLeft: number) => {
    const element = scrollRef.current
    if (!element) return
    const maxScrollLeft = Math.max(element.scrollWidth - element.clientWidth, 0)
    element.scrollLeft = Math.min(Math.max(scrollLeft, 0), maxScrollLeft)
    updateScrollState()
  }, [updateScrollState])

  useEffect(() => {
    const element = scrollRef.current
    if (!element) return

    updateScrollState()

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateScrollState)
      : null

    resizeObserver?.observe(element)
    if (element.firstElementChild) {
      resizeObserver?.observe(element.firstElementChild)
    }

    element.addEventListener('scroll', updateScrollState, { passive: true })
    window.addEventListener('resize', updateScrollState)
    return () => {
      resizeObserver?.disconnect()
      element.removeEventListener('scroll', updateScrollState)
      window.removeEventListener('resize', updateScrollState)
    }
  }, [updateScrollState, ...deps])

  return { scrollRef, isScrollable, scrollState, setScrollLeft }
}

function HorizontalScrollBar(props: {
  isScrollable: boolean
  scrollState: {
    clientWidth: number
    scrollLeft: number
    scrollWidth: number
  }
  onScrollLeftChange: (scrollLeft: number) => void
}) {
  const { isScrollable, scrollState, onScrollLeftChange } = props
  const trackRef = useRef<HTMLDivElement>(null)
  const dragStateRef = useRef<{
    startClientX: number
    startScrollLeft: number
  }>()
  const maxScrollLeft = Math.max(scrollState.scrollWidth - scrollState.clientWidth, 0)
  const thumbWidth = scrollState.scrollWidth
    ? Math.min(Math.max((scrollState.clientWidth / scrollState.scrollWidth) * scrollState.clientWidth, 32), scrollState.clientWidth - HORIZONTAL_SCROLL_THUMB_INSET * 2)
    : scrollState.clientWidth
  const thumbLeft = maxScrollLeft
    ? HORIZONTAL_SCROLL_THUMB_INSET + (scrollState.scrollLeft / maxScrollLeft) * Math.max(scrollState.clientWidth - thumbWidth - HORIZONTAL_SCROLL_THUMB_INSET * 2, 0)
    : HORIZONTAL_SCROLL_THUMB_INSET

  const syncScrollFromClientX = useCallback((clientX: number) => {
    const track = trackRef.current
    const dragState = dragStateRef.current
    if (!track || !dragState) return
    const trackScrollableWidth = Math.max(track.clientWidth - thumbWidth - HORIZONTAL_SCROLL_THUMB_INSET * 2, 0)
    if (trackScrollableWidth <= 0) return
    const deltaX = clientX - dragState.startClientX
    onScrollLeftChange(dragState.startScrollLeft + (deltaX / trackScrollableWidth) * maxScrollLeft)
  }, [maxScrollLeft, onScrollLeftChange, thumbWidth])

  if (!isScrollable) {
    return null
  }

  return (
    <div
      className={styles.horizontalScrollBar}
      ref={trackRef}
      style={{ width: scrollState.clientWidth }}
    >
      <div
        className={styles.horizontalScrollThumb}
        style={{
          left: thumbLeft,
          width: thumbWidth,
        }}
        onPointerDown={(event) => {
          event.preventDefault()
          dragStateRef.current = {
            startClientX: event.clientX,
            startScrollLeft: scrollState.scrollLeft,
          }
          const handlePointerMove = (moveEvent: PointerEvent) => {
            syncScrollFromClientX(moveEvent.clientX)
          }
          const handlePointerUp = () => {
            dragStateRef.current = undefined
            document.removeEventListener('pointermove', handlePointerMove)
            document.removeEventListener('pointerup', handlePointerUp)
          }
          document.addEventListener('pointermove', handlePointerMove)
          document.addEventListener('pointerup', handlePointerUp)
        }}
      >
        <span className={styles.horizontalScrollThumbIcon} aria-hidden='true'>
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
        </span>
      </div>
    </div>
  )
}

function compareSortableScores(
  scoreA: number | string,
  scoreB: number | string,
  direction: Exclude<MetricSortDirection, 'origin'>
) {
  const hasScoreA = typeof scoreA === 'number'
  const hasScoreB = typeof scoreB === 'number'
  if (!hasScoreA || !hasScoreB) {
    if (hasScoreA) return -1
    if (hasScoreB) return 1
    return 0
  }
  return direction === 'asc'
    ? scoreA - scoreB
    : scoreB - scoreA
}

function getProjectTotalScore(project: compare.CompareProject) {
  if (project.status === 'completed' && isValidMetricScore(project.score) && project.score !== 0) {
    return project.score
  }
  return '-'
}

function getProjectSortScore(
  project: compare.CompareProject,
  sortKey: string
) {
  return sortKey === 'score'
    ? getProjectTotalScore(project)
    : getMetricScore(project, sortKey)
}

function getMetricScore(
  project: compare.CompareProject,
  metricKey: string
) {
  const field = METRIC_FIELD_MAP[metricKey]
  const score = field ? project.metrics?.[field]?.score : undefined
  return isValidMetricScore(score) ? score : '-'
}

function hasVisibleMetricScore(
  projects: compare.CompareProject[],
  metricKey: string
) {
  return projects.some((project) => typeof getMetricScore(project, metricKey) === 'number')
}

function hasMixedPortraitCreativeProjects(
  productType: ProjectModels.ProjectCategory,
  projects: compare.CompareProject[]
) {
  if (!isPortraitProductCategory(productType)) {
    return false
  }
  return projects.some(isSplashCreativeProject) && projects.some((project) => !isSplashCreativeProject(project))
}

function isSplashCreativeProject(project: compare.CompareProject) {
  return project.durationCategory === 'five_to_ten'
}

function isValidMetricScore(score: unknown): score is number {
  return typeof score === 'number' && score !== -1 && score !== 0
}

function getMetricNorm(
  metricNorms: compare.CompareReportDetail['metricNorms'] | undefined,
  metricField: keyof compare.CompareProjectMetrics
) {
  const norm = metricNorms?.[metricField]
  return typeof norm === 'number' && norm !== 0 ? norm : '-'
}

function getScoreTrend(score: number, norm: number | string) {
  if (typeof norm !== 'number') {
    return undefined
  }
  if (score > norm + 3) {
    return 'up'
  }
  if (score < norm - 3) {
    return 'down'
  }
  return undefined
}

function formatNormValue(norm: number | string | undefined) {
  return norm ?? '-'
}

export default StatisticCompareInfoPage
